import { PaperPlane } from "@gravity-ui/icons";
import { Button, Tabs } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ErrorBlock, LoadingBlock, Page, PageHeader, StateChip } from "../components/ui";
import { useApp, useMe } from "../lib/queries";

import { KeysTab } from "./app/KeysTab";
import { MessagesTab } from "./app/MessagesTab";
import { OverviewTab } from "./app/OverviewTab";
import { ProfileTab } from "./app/ProfileTab";

const TABS = ["overview", "keys", "credentials", "messages"] as const;
type Tab = (typeof TABS)[number];

export function AppDetailPage() {
  const { appId = "" } = useParams();
  const { t } = useTranslation();
  const app = useApp(appId);
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const requested = params.get("tab") ?? "";
  const tab: Tab = (TABS as readonly string[]).includes(requested) ? (requested as Tab) : "overview";
  const canEdit = me?.role !== "viewer";

  if (app.isPending) return <LoadingBlock />;
  if (app.isError) return <ErrorBlock error={app.error} onRetry={() => app.refetch()} />;

  return (
    <Page>
      <PageHeader
        breadcrumb={
          <Link to="/apps" className="hover:text-foreground">
            {t("nav.apps")}
          </Link>
        }
        title={
          <span className="flex flex-wrap items-center gap-3">
            {app.data.name}
            <StateChip ok={app.data.status === "active"} label={t(`status.app.${app.data.status}`)} />
          </span>
        }
        actions={
          canEdit ? (
            <Button onPress={() => navigate(`/send?app=${appId}`)} isDisabled={app.data.status !== "active"}>
              <PaperPlane />
              {t("appDetail.sendTest")}
            </Button>
          ) : null
        }
      />

      <Tabs selectedKey={tab} onSelectionChange={(key) => setParams({ tab: String(key) }, { replace: true })}>
        <Tabs.ListContainer>
          <Tabs.List aria-label={app.data.name}>
            {TABS.map((id) => (
              <Tabs.Tab key={id} id={id}>
                {t(`appDetail.${id}`)}
                <Tabs.Indicator />
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs.ListContainer>
        <Tabs.Panel id="overview" className="px-0 pt-8">
          <OverviewTab
            app={app.data}
            canEdit={canEdit}
            isOwner={me?.role === "owner" || Boolean(me?.is_superadmin)}
          />
        </Tabs.Panel>
        <Tabs.Panel id="keys" className="px-0 pt-8">
          <KeysTab appId={appId} canEdit={canEdit} />
        </Tabs.Panel>
        <Tabs.Panel id="credentials" className="px-0 pt-8">
          <ProfileTab app={app.data} canEdit={canEdit} />
        </Tabs.Panel>
        <Tabs.Panel id="messages" className="px-0 pt-8">
          <MessagesTab appId={appId} />
        </Tabs.Panel>
      </Tabs>
    </Page>
  );
}
